import { memo } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
} from '@mui/material'

function EditProductDialog({
  open,
  formData,
  categories,
  divisions,
  loading,
  error,
  onClose,
  onSave,
  onChange,
  onClearError,
  fieldErrors = {},
}) {
  const packSizeFieldError = fieldErrors.pack_size
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>Edit Product</DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" onClose={onClearError} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Product Name"
            value={formData.product_name || ''}
            onChange={(e) => onChange('product_name', e.target.value)}
            fullWidth
            size="small"
            disabled={loading}
          />

          <TextField
            label="Description"
            value={formData.description || ''}
            onChange={(e) => onChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
            size="small"
            disabled={loading}
          />

          <TextField
            label="Article Code"
            value={formData.sku || ''}
            onChange={(e) => onChange('sku', e.target.value)}
            fullWidth
            size="small"
            disabled={loading || formData.source === 'external'}
          />

          <FormControl fullWidth size="small" disabled={loading}>
            <InputLabel>Source</InputLabel>
            <Select
              label="Source"
              value={formData.source || ''}
              onChange={(e) => onChange('source', e.target.value)}
            >
              <MenuItem value="shwapno">shwapno</MenuItem>
              <MenuItem value="external">external</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={loading}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={formData.category_id || ''}
              onChange={(e) => onChange('category_id', e.target.value)}
            >
              <MenuItem value="">
                <em>Select Category</em>
              </MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={loading}>
            <InputLabel>Division</InputLabel>
            <Select
              label="Division"
              value={formData.division_id || ''}
              onChange={(e) => onChange('division_id', e.target.value)}
            >
              <MenuItem value="">
                <em>Select Division</em>
              </MenuItem>
              {(divisions || []).map((division) => (
                <MenuItem key={division.id} value={division.id}>
                  {division.name} ({division.slug})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Logo Image URL"
            value={formData.image_logo_url || ''}
            onChange={(e) => onChange('image_logo_url', e.target.value)}
            fullWidth
            size="small"
            placeholder="https://example.com/logo.jpg"
            disabled={loading}
            helperText="Main logo/thumbnail image"
          />

          <TextField
            label="Portrait Image URL"
            value={formData.image_portrait_url || ''}
            onChange={(e) => onChange('image_portrait_url', e.target.value)}
            fullWidth
            size="small"
            placeholder="https://example.com/portrait.jpg"
            disabled={loading}
            helperText="Portrait orientation image"
          />

          <TextField
            label="Banner Image URL"
            value={formData.image_banner_url || ''}
            onChange={(e) => onChange('image_banner_url', e.target.value)}
            fullWidth
            size="small"
            placeholder="https://example.com/banner.jpg"
            disabled={loading}
            helperText="Wide banner image"
          />

          <TextField
            label="Additional Images"
            value={formData.additional_images || ''}
            onChange={(e) => onChange('additional_images', e.target.value)}
            fullWidth
            size="small"
            placeholder='["https://example.com/img1.jpg", "https://example.com/img2.jpg"]'
            disabled={loading}
            helperText="JSON array of image URLs or comma-separated list"
            multiline
            rows={2}
          />

          <TextField
            label="Brand"
            value={formData.brand || ''}
            onChange={(e) => onChange('brand', e.target.value)}
            fullWidth
            size="small"
            placeholder="Product brand"
            disabled={loading}
          />

          <TextField
            label="TP/MAP"
            value={formData.tp_map || ''}
            onChange={(e) => onChange('tp_map', e.target.value)}
            fullWidth
            size="small"
            placeholder="Optional trade price / MAP reference"
            disabled={loading}
            helperText="Optional"
          />

          <TextField
            label="Company Rate"
            type="number"
            value={formData.company_rate || ''}
            onChange={(e) => onChange('company_rate', parseFloat(e.target.value))}
            fullWidth
            size="small"
            disabled={loading}
            inputProps={{ step: '0.01' }}
            helperText="Optional"
          />

          <TextField
            label="VAT %"
            type="number"
            value={formData.vat ?? ''}
            onChange={(e) => onChange('vat', e.target.value)}
            fullWidth
            size="small"
            disabled={loading}
            inputProps={{ step: '0.01', min: 0, max: 100 }}
            helperText="0–100, up to 2 decimals. Leave blank to keep the existing value."
          />

          <FormControl fullWidth size="small" disabled={loading}>
            <InputLabel>Tax Category</InputLabel>
            <Select
              value={formData.tax_category || ''}
              onChange={(e) => onChange('tax_category', e.target.value)}
              label="Tax Category"
            >
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="reduced">Reduced</MenuItem>
              <MenuItem value="zero">Zero Rate</MenuItem>
              <MenuItem value="exempt">Exempt</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" disabled={loading}>
            <InputLabel>Unit of Measure</InputLabel>
            <Select
              value={formData.unit_of_measure || ''}
              onChange={(e) => onChange('unit_of_measure', e.target.value)}
              label="Unit of Measure"
            >
              <MenuItem value="pcs">Pieces</MenuItem>
              <MenuItem value="kg">Kilograms</MenuItem>
              <MenuItem value="g">Grams</MenuItem>
              <MenuItem value="l">Liters</MenuItem>
              <MenuItem value="ml">Milliliters</MenuItem>
              <MenuItem value="m">Meters</MenuItem>
              <MenuItem value="cm">Centimeters</MenuItem>
              <MenuItem value="pack">Pack</MenuItem>
              <MenuItem value="box">Box</MenuItem>
              <MenuItem value="dozen">Dozen</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Pack Size"
            type="number"
            value={formData.pack_size ?? ''}
            onChange={(e) => onChange('pack_size', e.target.value)}
            fullWidth
            size="small"
            disabled={loading}
            inputProps={{ min: 1, step: 1 }}
            error={Boolean(packSizeFieldError)}
            helperText={
              packSizeFieldError ||
              'Optional — units per pack (whole number greater than 0)'
            }
          />

          <FormControl fullWidth size="small" disabled={loading}>
            <InputLabel>Pack Type</InputLabel>
            <Select
              value={formData.pack_type || ''}
              onChange={(e) => onChange('pack_type', e.target.value)}
              label="Pack Type"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="carton">Carton</MenuItem>
              <MenuItem value="poly">Poly</MenuItem>
              <MenuItem value="sack">Sack</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active !== false}
                onChange={(e) => onChange('is_active', e.target.checked)}
                disabled={loading}
              />
            }
            label="Product is Active"
          />
          {formData.source !== 'external' && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_purchasable !== false}
                    onChange={(e) => onChange('is_purchasable', e.target.checked)}
                    disabled={loading}
                  />
                }
                label="Available for purchase"
              />
              <Typography variant="caption" sx={{ color: '#666', mt: -1 }}>
                When off, the product is hidden from purchase catalog and cannot be added to purchase
                orders. POS / sales are unaffected.
              </Typography>
            </>
          )}
          {formData.source === 'external' && (
            <Typography variant="caption" sx={{ color: '#666' }}>
              External products are not available for purchase orders. Purchasable cannot be enabled.
            </Typography>
          )}

          {formData.source !== 'external' && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.dsd === true}
                  onChange={(e) => onChange('dsd', e.target.checked)}
                  disabled={loading}
                />
              }
              label="DSD"
            />
          )}
          {formData.source === 'external' && (
            <Typography variant="caption" sx={{ color: '#666' }}>
              DSD cannot be enabled for external products.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={loading}
          sx={{ bgcolor: '#e90000', '&:hover': { bgcolor: '#1b5e20' } }}
        >
          {loading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(EditProductDialog)
